import { useEffect } from "react";

/**
 * Applies scroll-tied parallax/transform to any element with `data-parallax`.
 * Usage: <div data-parallax="0.18" /> — value = px translated per pixel of scroll
 * relative to the element entering the viewport.
 * Optional: data-parallax-rotate="0.02" (deg per scroll px), data-parallax-scale="0.0002".
 */
export default function useScrollParallax() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const els = Array.from(document.querySelectorAll("[data-parallax]"));
    if (!els.length) return;

    let raf = 0;
    const update = () => {
      const vh = window.innerHeight;
      for (const el of els) {
        const rect = el.getBoundingClientRect();
        // progress: -1 when fully below viewport, 0 when centered, 1 when fully above
        const center = rect.top + rect.height / 2;
        const progress = (center - vh / 2) / vh; // approx -1..1 across viewport
        const ty = (Number(el.dataset.parallax) || 0) * progress * vh;
        const rot = (Number(el.dataset.parallaxRotate) || 0) * progress * 60;
        const scale = 1 + (Number(el.dataset.parallaxScale) || 0) * progress * 100;
        el.style.transform = `translate3d(0, ${ty.toFixed(2)}px, 0) rotate(${rot.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
      }
    };

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
}
